import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth-context";
import SourcesBoard from "@/components/landing/SourcesBoard";
import Pipeline from "@/components/landing/Pipeline";
import RadarTimeline from "@/components/landing/RadarTimeline";
import McpFlow from "@/components/landing/McpFlow";
import TruthStack from "@/components/landing/TruthStack";
import CoverageBand from "@/components/landing/CoverageBand";
import { useCoverage } from "@/lib/coverage";
import Workflow from "@/components/landing/Workflow";
import ShowReel from "@/components/landing/ShowReel";
import SponsorshipTruth from "@/components/landing/SponsorshipTruth";
import SectionRail from "@/components/landing/SectionRail";
import HeroScanField from "@/components/landing/HeroScanField";
import Stage from "@/components/landing/Stage";
import Reveal from "@/components/landing/Reveal";
import SectionMark from "@/components/landing/SectionMark";
import {
  IconScore, IconRewrite, IconLetter, IconBoard, IconShield, IconPace,
  IconRadar, IconTerminal, IconRise, IconArrowRise,
} from "@/components/landing/icons";
import PublicNav from "@/components/PublicNav";
import AnnouncementBar from "@/components/AnnouncementBar";
import ClosingCTA from "@/components/public/ClosingCTA";
import ScrollProgressBar from "@/components/ScrollProgressBar";
import BackToTop from "@/components/BackToTop";

/** The rail's stops, in reading order. `truth` exists only once the live
 *  figures have arrived, so the list is filtered at render and the rail
 *  re-resolves when it changes. */
const RAIL_SECTIONS = [
  { id: "workflow", label: "How it works" },
  { id: "features", label: "Why a score matters" },
  { id: "different", label: "What nobody else does" },
  { id: "truth", label: "What postings say" },
  { id: "trust", label: "Trust & safety" },
  { id: "free", label: "Free plan" },
];

// Shown until /billing/tiers answers. Deliberately conservative — understating
// the free plan is recoverable, overstating it is not.
const FREE_FALLBACK = [
  "1 base resume",
  "5 tailored resumes / month",
  "5 cover letters / month",
  "Pipeline tracker",
  "PDF & DOCX export",
];

const TRUST_POINTS = [
  "AI only rephrases and emphasizes your real experience.",
  "You review and approve every resume, letter, and application.",
  "Resumes are stored privately — never exposed publicly.",
  "Delete your resumes or your account at any time.",
  "No bulk auto-apply. No spamming job boards.",
];

const FEATURES = [
  { icon: IconScore, title: "ATS keyword gap analysis", body: "Every job gets a 0–100 score with matching and missing keywords called out — no more guessing what a screener sees." },
  { icon: IconRewrite, title: "Truthful resume tailoring", body: "AI rephrases, reorders, and emphasizes your real background. It never invents companies, degrees, or experience." },
  { icon: IconLetter, title: "Cover letters that read human", body: "Five tones, three paragraphs, zero AI-slop. Approve, edit, or regenerate before you send." },
  { icon: IconBoard, title: "One pipeline for everything", body: "Kanban tracker — Saved, Tailored, Applied, Interviewing, Offer. Follow-ups and match scores in one view." },
  { icon: IconShield, title: "Sponsorship, stated plainly", body: "Each posting's sponsorship stance in its own words, and the employer's H-1B filings from U.S. Department of Labor records — with the caveat that filings are not visas." },
  { icon: IconShield, title: "Human-in-the-loop, always", body: "No spammy auto-submit. You approve every resume, letter and application before it goes anywhere." },
  { icon: IconPace, title: "Apply faster, not sloppier", body: "Skip the 40-minute per-application ritual. Prep a tailored packet in under 5 minutes." },
];

/**
 * The word the sentence is actually about, set in the one serif on the site.
 *
 * The rule is narrow on purpose: the serif marks the HUMAN word — opportunity,
 * assistant, free — never the mechanical one. Four occurrences on the page,
 * the first and last both "opportunity", so the page closes where it opened.
 * A fifth would make it a decoration rather than a device.
 */
function Hope({ children, tone = "light" }) {
  return (
    <em className={`font-display not-italic ${tone === "dark" ? "text-dawn-300" : "text-dawn-800"}`}>
      {children}
    </em>
  );
}

export default function Landing() {
  const { user } = useAuth();
  const [heroPlaying, setHeroPlaying] = useState(false);
  useEffect(() => {
    document.documentElement.classList.add("snap-hero");
    return () => document.documentElement.classList.remove("snap-hero");
  }, []);
  const navigate = useNavigate();
  // Read from the API rather than retyped: these are the same strings the
  // pricing page shows and billing.py enforces.
  const [freeFeatures, setFreeFeatures] = useState(FREE_FALLBACK);
  // Live index figures, shared with the coverage band so the two cannot drift.
  const coverage = useCoverage();
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/billing/tiers");
        const f = data?.tiers?.free?.features;
        if (Array.isArray(f) && f.length) setFreeFeatures(f);
      } catch {
        /* Fallback copy stays; a marketing page must not blank on an API blip. */
      }
    })();
  }, []);

  return (
    <div className="min-h-screen bg-paper text-stone-950">
      {/* Six sections and a nav menu is a long way to tab past to reach the one
          control that matters. */}
      <a
        href="#hero-resume"
        className="sr-only focus:not-sr-only focus:fixed focus:z-50 focus:top-3 focus:left-3 focus:px-4 focus:py-2 focus:bg-night focus:text-white focus:rounded-sm"
      >
        Skip to the free resume scan
      </a>
      {/* Landing carries its own chrome rather than PublicLayout, so the scroll
          controls have to be mounted here explicitly. */}
      <ScrollProgressBar />
      <AnnouncementBar />
      {/* overlay: the hero opens on the dark stage, so a solid white bar would
          cut a hard line across it. Goes solid once the page scrolls. */}
      <PublicNav overlay />

      {/* ── Hero · phase 0 · the hour before dawn ──────────────────────────
          The darkest, coldest point on the page. Everything below it is
          further into the morning; nothing is ever darker than this again. */}
      <Stage phase={0} tint="brand" className="snap-target">
        <div className="relative max-w-6xl mx-auto px-6 pt-16 pb-14 lg:pt-24 lg:pb-16">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/15 bg-white/[0.04] text-[11px] tracking-[0.18em] uppercase font-bold text-white/75">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
            Nothing invented — and you can check
          </div>

          <h1 className="mt-7 font-heading font-bold tracking-[-0.045em] leading-[0.92] text-[clamp(2.6rem,7.2vw,6rem)]">
            Your next <Hope tone="dark">opportunity</Hope>
            <br />
            starts here.
          </h1>

          <div className="mt-10 lg:mt-12 grid lg:grid-cols-12 gap-10 lg:gap-14 items-stretch">
            <div className="lg:col-span-7 flex flex-col">
              <p className="text-lg text-white/70 leading-relaxed max-w-xl">
                Find roles the moment companies post them. Tailor your resume to
                each one — reworded from your real history, never invented.
              </p>
              <HeroScanField dark />
              <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm">
                <button
                  onClick={() => navigate(user ? "/dashboard" : "/signup")}
                  className="font-medium text-white underline underline-offset-4 decoration-white/40 hover:decoration-dawn-300 hover:text-dawn-100"
                  data-testid="hero-secondary-cta"
                >
                  {user ? "Open your dashboard" : "Create a free account"}
                </button>
                <a href="#workflow" className="text-white/60 hover:text-white underline underline-offset-4 decoration-white/25">
                  See how it works
                </a>
              </div>
              {/* mt-auto: bottom-aligns with the stack in the other column, so
                  the hero has two columns of equal weight rather than one tall
                  one and one short one with a hole under it. */}
              <div className="mt-auto pt-10 lg:pt-12 border-t border-white/10">
                <CoverageBand dark />
              </div>
            </div>

            {/* The demo, as the hero's counterweight: a play affordance above
                the fold. The resume stack that stood here (three drafts
                climbing toward a higher score) now opens the Features page. */}
            <div className="lg:col-span-5 flex items-center">
              {/* Press play, and it plays here. The first cut jumped to the
                  video band below; a poster that sends you elsewhere is a
                  link wearing a play button. */}
              {heroPlaying ? (
                <video
                  className="block w-full aspect-video rounded-sm border border-white/15 bg-night shadow-[0_30px_80px_rgba(0,0,0,0.5)]"
                  src="/media/full-demo.mp4"
                  poster="/media/full-demo.jpg"
                  controls
                  autoPlay
                  playsInline
                  data-testid="hero-demo-video"
                />
              ) : (
                <button
                  type="button"
                  onClick={() => setHeroPlaying(true)}
                  className="group relative block w-full rounded-sm overflow-hidden border border-white/15 shadow-[0_30px_80px_rgba(0,0,0,0.5)] text-left"
                  aria-label="Play the full demo, 1 minute 52 seconds"
                  data-testid="hero-demo-poster"
                >
                  <img src="/media/full-demo.jpg" alt="" className="block w-full aspect-video object-cover" width="1280" height="720" />
                  <span className="absolute inset-0 grid place-items-center bg-gradient-to-t from-night/60 to-night/5" aria-hidden="true">
                    <span className="w-[72px] h-[72px] rounded-full bg-dawn-500 grid place-items-center ring-[12px] ring-dawn-500/20 transition-transform group-hover:scale-105">
                      <svg width="26" height="26" viewBox="0 0 24 24" fill="#0a0a0b"><path d="M8 5v14l11-7z" /></svg>
                    </span>
                  </span>
                  <span className="absolute left-4 bottom-3.5 text-[11px] tracking-[0.2em] uppercase font-bold text-white" aria-hidden="true">Watch the demo · 1:52</span>
                </button>
              )}
            </div>
          </div>

        </div>
      </Stage>

      {/* ── See it · the product moving, before the argument for it ──────── */}
      <ShowReel />

      {/* Desktop only: one dot per section, the sun rising up the track as
          the reader goes. A navigation aid — never scroll hijacking. */}
      <SectionRail sections={coverage?.sponsorship ? RAIL_SECTIONS : RAIL_SECTIONS.filter((x) => x.id !== "truth")} />

      {/* ── 01 · Discover / Prepare / Apply · first light on the ground ──── */}
      <section id="workflow" className="scroll-mt-16 border-b border-sand bg-paper">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-2xl">
            <SectionMark index={1} total={6}>How it works</SectionMark>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight text-stone-950">
              Discover. Prepare. Apply.
            </h2>
          </div>
          <Reveal delay={80}>
            <div className="mt-14">
              <Workflow />
            </div>
          </Reveal>
          {/* The same loop, as the four artifacts it produces. This was its
              own section ("Four steps. Under five minutes.") until the demo
              video under the hero started promising the walkthrough too —
              two sections for one idea read as padding. */}
          <Reveal delay={160}>
            <div className="mt-16 pt-12 border-t border-sand">
              <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600">Four steps. Under five minutes.</div>
              <div className="mt-8">
                <Pipeline />
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── 02 · Why a score matters · phase 0.42, the sky starting to turn ─
          The funnel is the argument; the copy just names it. */}
      <Stage id="features" className="scroll-mt-16" phase={0.42} tint="brand">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div>
            <SectionMark index={2} total={6} tone="dark">Why a score matters</SectionMark>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight">
              Your resume is read by software first.
            </h2>
            <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
              Before a person ever sees it, an applicant tracking system decides
              whether your words match the posting’s words. That is a solvable
              problem — but only if you can see what it sees.
            </p>
            {/* Seven features in columns rather than one long list: beside
                the old funnel the column ran past the graphic and left a
                dark hole; over the board it reads as a spec sheet. */}
            <ul className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-5">
              {FEATURES.map((f) => (
                <li key={f.title} className="flex gap-3.5">
                  <f.icon className="w-[18px] h-[18px] mt-0.5 shrink-0 text-dawn-300" />
                  <div>
                    <div className="text-sm font-medium text-white">{f.title}</div>
                    <div className="text-sm text-white/60 leading-relaxed">{f.body}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
          {coverage?.sources && (
            <Reveal delay={120}>
              <div className="mt-14">
                <SourcesBoard sources={coverage.sources} totals={coverage} />
              </div>
            </Reveal>
          )}
        </div>
      </Stage>

      {/* ── 03 · The two things nobody else has ─────────────────────────────
          Given their own section rather than a tile in the FEATURES list: a
          generic card cannot carry a claim whose whole value is that it is
          specific. */}
      <section id="different" className="scroll-mt-16 border-b border-sand bg-paper">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
          <div className="max-w-2xl">
            <SectionMark index={3} total={6}>What nobody else does</SectionMark>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight text-stone-950">
              Two things you won’t find in another job tool.
            </h2>
          </div>

          <div className="mt-12 grid lg:grid-cols-2 gap-5">
            <Reveal delay={60} className="h-full">
              <div className="h-full flex flex-col border border-sand rounded-sm bg-white overflow-hidden" data-testid="landing-radar">
                <div className="p-8 flex-1">
                  <div className="flex items-center gap-2.5">
                    <IconRadar className="w-5 h-5 text-brand" />
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600">Job Radar</div>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-stone-950">
                    Roles straight from the company’s own career page.
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                    {/* The count is read, not typed: it said "nine" while the
                        index read 18. Absent, the sentence still stands. */}
                    {`Most tools resell the same job-board feed everyone else sees. Radar watches company ATS boards directly${
                      coverage?.ats_systems ? ` — ${coverage.ats_systems} of them` : ""
                    }, including Greenhouse, Lever, Ashby, Workable and Workday, so you see a role when the company posts it, not when an aggregator gets around to indexing it.`}
                  </p>
                  <ul className="mt-5 space-y-2">
                    {[
                      "Save a search once; it keeps running in the background",
                      "Deduplicated, so the same role never reaches you twice",
                      "A digest email when something genuinely new appears",
                    ].map((t) => (
                      <li key={t} className="flex gap-2.5 text-sm text-stone-800">
                        <IconRise className="w-4 h-4 mt-0.5 text-verified shrink-0" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-8 py-7 bg-night">
                  <RadarTimeline />
                </div>
              </div>
            </Reveal>

            <Reveal delay={140} className="h-full">
              <div className="h-full flex flex-col border border-sand rounded-sm bg-white overflow-hidden" data-testid="landing-mcp">
                <div className="p-8 flex-1">
                  <div className="flex items-center gap-2.5">
                    <IconTerminal className="w-5 h-5 text-brand" />
                    <div className="text-xs tracking-[0.2em] uppercase font-bold text-stone-600">MCP server</div>
                  </div>
                  <h3 className="mt-4 font-heading text-2xl font-bold tracking-tight text-stone-950">
                    Your own AI agent can drive it.
                  </h3>
                  <p className="mt-3 text-sm text-stone-600 leading-relaxed">
                    NextOpp ships a Model Context Protocol server, so Claude — or any
                    MCP-capable assistant — can score your resume, tailor it, and read
                    your pipeline. Under exactly the same rules you get: it never
                    invents experience, and it never submits an application.
                  </p>
                  <ul className="mt-5 space-y-2">
                    {[
                      "Read your pipeline and match scores from your own tools",
                      "Every write still needs your approval in the app",
                      "Scoped access tokens you can revoke at any time",
                    ].map((t) => (
                      <li key={t} className="flex gap-2.5 text-sm text-stone-800">
                        <IconRise className="w-4 h-4 mt-0.5 text-verified shrink-0" />{t}
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="px-8 py-7 bg-night">
                  <McpFlow />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ── 04 · What postings actually say · lit ground ──────────────────
          The one number on the page that is NextOpp's own: counted from the
          live index, classified from each posting's words. Rendered only when
          the coverage figures have arrived — a heading over nothing is worse
          than no section. */}
      {coverage?.sponsorship && (
        <section id="truth" className="scroll-mt-16 border-b border-sand bg-paper" data-testid="landing-truth">
          <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20">
            <div className="max-w-2xl">
              <SectionMark index={4} total={6}>What postings actually say</SectionMark>
              <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight text-stone-950">
                Sponsorship, in the poster's own words.
              </h2>
              <p className="mt-5 text-lg text-stone-600 leading-relaxed">
                Set your work authorization once and every result carries the posting's stance, with the sentence it came from.
                Most say nothing — and NextOpp shows that as exactly that, never as a yes.
              </p>
            </div>
            <Reveal delay={80}>
              <div className="mt-12">
                <SponsorshipTruth counts={coverage.sponsorship} />
              </div>
            </Reveal>
          </div>
        </section>
      )}


      {/* ── 05 · Trust & safety · phase 0.78, the sky gone warm ───────────
          The brand’s whole argument, lit by the same sun as everything else so
          the 3D object belongs to the page rather than sitting on top of it. */}
      <Stage id="trust" className="scroll-mt-16" phase={0.78} tint="verified">
        <div className="max-w-7xl mx-auto px-6 py-12 lg:py-16 lg:py-20 grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <div className="lg:col-span-5">
            <SectionMark index={5} total={6} tone="dark">Trust &amp; safety</SectionMark>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight">
              <Hope tone="dark">Assistant</Hope>, not autopilot.
            </h2>
            <p className="mt-5 text-lg text-white/70 leading-relaxed">
              Your facts go in. The wording changes. The facts come out the same
              — and NextOpp shows you, bullet by bullet, that they did.
            </p>
            <ul className="mt-8 space-y-3.5">
              {TRUST_POINTS.map((t) => (
                <li key={t} className="flex gap-3 text-sm text-white/75">
                  <IconRise className="w-4 h-4 mt-0.5 text-emerald-300 shrink-0" />
                  <span>{t}</span>
                </li>
              ))}
            </ul>
          </div>

          <div className="lg:col-span-7 flex justify-center">
            <TruthStack />
          </div>
        </div>
      </Stage>

      {/* ── 06 · Free plan ───────────────────────────────────────────────
          Pulled from /billing/tiers rather than retyped, so the numbers on the
          landing page cannot drift from what is enforced. */}
      <section id="free" className="scroll-mt-16 border-b border-sand bg-paper">
        <div className="max-w-7xl mx-auto px-6 py-16 lg:py-20 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <SectionMark index={6} total={6}>Free plan</SectionMark>
            <h2 className="mt-4 font-heading text-4xl lg:text-5xl font-bold tracking-tight text-stone-950">
              Start <Hope>free</Hope>. No card.
            </h2>
            <p className="mt-5 text-lg text-stone-600 leading-relaxed max-w-lg">
              The free plan is a real plan, not a countdown. Scan without an
              account at all; make one when you want to keep what you find.
            </p>
            <Link to="/pricing" className="mt-6 inline-flex items-center gap-1.5 text-sm font-medium text-brand hover:underline underline-offset-4">
              Compare all plans <IconArrowRise className="w-4 h-4" />
            </Link>
          </div>
          <Reveal delay={80}>
            <div className="border border-sand rounded-sm bg-white p-8" data-testid="landing-free-plan">
              <div className="flex items-baseline gap-2">
                <span className="font-heading text-5xl font-bold tracking-tighter text-stone-950">$0</span>
                <span className="text-stone-600">/ month</span>
              </div>
              <ul className="mt-6 space-y-2.5">
                {freeFeatures.map((f) => (
                  <li key={f} className="flex gap-2.5 text-sm text-stone-800">
                    <IconRise className="w-4 h-4 mt-0.5 text-verified shrink-0" />{f}
                  </li>
                ))}
              </ul>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ── Morning ──────────────────────────────────────────────────────
          The page has spent six sections climbing out of the dark, so it
          closes in full daylight — the same ClosingCTA every other public page
          now ends on. */}
      <ClosingCTA
        title={<>Find your next <em className="font-display not-italic text-dawn-800">opportunity</em>.</>}
        lede="Every application should be your best shot at the role. NextOpp makes that the default."
        to={user ? "/dashboard" : "/signup"}
        label={user ? "Open your dashboard" : "Start free — no card"}
        testId="footer-cta"
        footer={
          <p className="mt-12 text-xs text-stone-600">
            © {new Date().getFullYear()} NextOpp.io · Made for job seekers who care about quality.
          </p>
        }
      />
      <BackToTop />
    </div>
  );
}
